import { gql } from "@urql/core";
import { useMutation } from "@urql/vue";
import graphqlClient from "@/graphql/client";

export type FlowShareTarget = {
  spaceId: string;
  sectionId?: string | null;
};

export type FlowShareViolation = {
  scope?: string;
  code?: string;
  kind?: string | null;
  flowItemId?: string | number | null;
  documentId?: string | number | null;
  pageId?: string | number | null;
  videoId?: string | number | null;
  title?: string | null;
  spaceId?: string | number | null;
  sectionId?: string | number | null;
  spaceName?: string | null;
};

export type FlowShareTargetResult = {
  spaceId?: string | number | null;
  sectionId?: string | number | null;
  spaceName?: string | null;
  status?: string | null;
  documentSpaceItemId?: string | number | null;
};

export type FlowShareResult = {
  canCurrentUserShare?: boolean | null;
  downloadAvailableForVisitor?: boolean | null;
  violations?: unknown;
  existingSpaceNames?: Array<string | null> | null;
  targetResults?: unknown;
  createdDocumentSpaceItemIds?: Array<string | number | null> | null;
};

type AddFlowToMultipleSpacesMutation = {
  addFlowToMultipleSpaces?: {
    results?: Array<FlowShareResult | null> | null;
  } | null;
};

type AddFlowToMultipleSpacesVariables = {
  flowId: string;
  targets: FlowShareTarget[];
};

type PreviewFlowShareToSpacesQuery = {
  previewFlowShareToSpaces?: {
    nodes?: Array<FlowShareResult | null> | null;
  } | null;
};

type PreviewFlowShareToSpacesVariables = {
  flowId: string;
  targets?: FlowShareTarget[] | null;
};

const AddFlowToMultipleSpacesDocument = gql`
  mutation AddFlowToMultipleSpaces(
    $flowId: BigInt!
    $targets: [IdsOfSpaceAndSectionInput]
  ) {
    addFlowToMultipleSpaces(input: { flowId: $flowId, targets: $targets }) {
      results {
        canCurrentUserShare
        downloadAvailableForVisitor
        violations
        existingSpaceNames
        targetResults
        createdDocumentSpaceItemIds
      }
    }
  }
`;

const PreviewFlowShareToSpacesDocument = gql`
  query PreviewFlowShareToSpaces(
    $flowId: BigInt!
    $targets: [IdsOfSpaceAndSectionInput]
  ) {
    previewFlowShareToSpaces(flowId: $flowId, targets: $targets, first: 1) {
      nodes {
        canCurrentUserShare
        downloadAvailableForVisitor
        violations
        existingSpaceNames
        targetResults
        createdDocumentSpaceItemIds
      }
    }
  }
`;

export async function previewFlowShareToSpaces(
  flowId: string,
  targets?: FlowShareTarget[] | null,
) {
  const response = await graphqlClient
    .query<PreviewFlowShareToSpacesQuery, PreviewFlowShareToSpacesVariables>(
      PreviewFlowShareToSpacesDocument,
      { flowId, targets },
      { requestPolicy: "network-only" },
    )
    .toPromise();

  if (response.error) {
    throw response.error;
  }

  return response.data?.previewFlowShareToSpaces?.nodes?.[0] ?? null;
}

export function useFlowSpaceShareMutation() {
  const { executeMutation, fetching } = useMutation<
    AddFlowToMultipleSpacesMutation,
    AddFlowToMultipleSpacesVariables
  >(AddFlowToMultipleSpacesDocument);

  async function shareFlowToSpaces(flowId: string, targets: FlowShareTarget[]) {
    const response = await executeMutation({ flowId, targets });

    if (response.error) {
      throw response.error;
    }

    return response.data?.addFlowToMultipleSpaces?.results?.[0] ?? null;
  }

  return {
    fetching,
    shareFlowToSpaces,
  };
}

export function normalizeFlowShareViolations(
  violations: unknown,
): FlowShareViolation[] {
  return Array.isArray(violations)
    ? violations.filter((v): v is FlowShareViolation => !!v && typeof v === "object")
    : [];
}

export function normalizeFlowShareTargetResults(
  targetResults: unknown,
): FlowShareTargetResult[] {
  return Array.isArray(targetResults)
    ? targetResults.filter((r): r is FlowShareTargetResult => !!r && typeof r === "object")
    : [];
}

export function flowShareCreatedItemIds(result: FlowShareResult | null) {
  return (result?.createdDocumentSpaceItemIds ?? []).filter((id) => id != null);
}

export function flowShareExistingSpaceNames(result: FlowShareResult | null) {
  const names = new Set<string>();

  for (const name of result?.existingSpaceNames ?? []) {
    if (name) names.add(name);
  }

  for (const target of normalizeFlowShareTargetResults(result?.targetResults)) {
    if (target.status === "already_present" && target.spaceName) {
      names.add(target.spaceName);
    }
  }

  return Array.from(names);
}

export function flowShareSucceeded(result: FlowShareResult | null) {
  return flowShareCreatedItemIds(result).length > 0;
}

export function firstBlockingFlowShareViolation(result: FlowShareResult | null) {
  const targetBlock = normalizeFlowShareTargetResults(result?.targetResults).find(
    (target) =>
      target.status &&
      !["created", "ready", "already_present"].includes(target.status),
  );

  if (targetBlock) {
    return {
      scope: "share",
      code: targetBlock.status ?? "blocked",
      spaceName: targetBlock.spaceName ?? null,
    };
  }

  return normalizeFlowShareViolations(result?.violations).find(
    (violation) => violation.scope !== "download",
  );
}

export function firstGlobalBlockingFlowShareViolation(result: FlowShareResult | null) {
  return normalizeFlowShareViolations(result?.violations).find(
    (violation) => violation.scope !== "download" && violation.code !== "no_targets",
  );
}

export function flowShareIsGloballyBlocked(result: FlowShareResult | null) {
  return !!firstGlobalBlockingFlowShareViolation(result);
}

export function describeFlowShareViolation(t: (key: string, params?: any) => string, violation?: FlowShareViolation) {
  if (!violation) {
    return t("customFlows.shareModal.blockReasons.default");
  }

  const title = violation.title || violation.spaceName || t("customFlows.shareModal.blockReasons.thisContent");

  switch (violation.code) {
    case "missing_feature_flag":
      return t("customFlows.shareModal.blockReasons.missingFeatureFlag");
    case "not_found_or_not_accessible":
    case "not_flow_creator":
    case "unauthenticated":
      return t("customFlows.shareModal.blockReasons.notAccessible");
    case "flow_has_no_visible_content":
      return t("customFlows.shareModal.blockReasons.noVisibleContent");
    case "document_expired":
      return t("customFlows.shareModal.blockReasons.expired", { title });
    case "document_not_current_revision":
      return t("customFlows.shareModal.blockReasons.notCurrentRevision", { title });
    case "space_not_editable":
      return t("customFlows.shareModal.blockReasons.spaceNotEditable", { title });
    case "section_not_in_space":
      return t("customFlows.shareModal.blockReasons.sectionNotInSpace", { title });
    case "page_not_shareable":
    case "video_not_shareable":
    case "legal_appendix_page_not_shareable":
      return t("customFlows.shareModal.blockReasons.notShareable", { title });
    case "page_not_supported_in_flows":
    case "video_not_supported_in_flows":
    case "legal_appendix_page_not_supported_in_flows":
      return t("customFlows.shareModal.blockReasons.notSupportedInFlows", { title });
    default:
      return t("customFlows.shareModal.blockReasons.default");
  }
}
